import { useCallback, useMemo, useState } from "react";
import { useWebSocketContext } from "../context/WebSocketContext";
import { EditWidgetV2Request, EditWidgetV2WebSocketMessage } from "../types";
import { AgentSelectionContext } from "../types/agent.types";
import { Widget } from "@/modules";

export const useEditWidgetV2WebSocket = () => {
    const { state, sendMessage, clearMessages, isReady } = useWebSocketContext();
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

    const messages = useMemo(() => {
        if (!currentSessionId) return [];
        return (state.messages as unknown as EditWidgetV2WebSocketMessage[]).filter(
            (msg) => msg.action === "edit-widget-v2" && msg.data?.sessionId === currentSessionId
        );
    }, [state.messages, currentSessionId]);

    const clearSessionMessages = useCallback(() => {
        if (!currentSessionId) return;
        clearMessages(
            (message) => message.action === "edit-widget-v2" && (message.data as unknown as EditWidgetV2WebSocketMessage["data"])?.sessionId === currentSessionId
        );
        setIsProcessing(false);
    }, [clearMessages, currentSessionId]);

    const sendRequest = useCallback(
        (widget: Widget, prompt: string, clarificationAnswers?: { title: string; values: string[] }[], selectionContext?: AgentSelectionContext) => {
            const sessionId = `edit-widget-v2-${Date.now()}`;
            setCurrentSessionId(sessionId);
            setIsProcessing(true);

            const { data: _data, ...widgetWithoutData } = widget as Widget & { data?: unknown };

            const request: EditWidgetV2Request = {
                action: "edit-widget-v2",
                sessionId,
                widget: widgetWithoutData,
                prompt,
                clarificationAnswers,
                selectionContext,
            };

            sendMessage(JSON.stringify({ ...request, timestamp: Date.now() }));
        },
        [sendMessage]
    );

    const resetProcessing = useCallback(() => setIsProcessing(false), []);

    return {
        isReady,
        isProcessing,
        error: state.error,
        messages,
        currentSessionId,
        sendRequest,
        clearSessionMessages,
        resetProcessing,
    };
};
